import gql from "graphql-tag";

export const GET_CHANNELS_ORGANIZATION_QUERY = gql`
    query GetChannelsOrganizationQuery($organizationId: ID!) {
        channels(organizationId: $organizationId) {
            channelId
            organizationId
            channelStatus
            domain
            createdAt
            metadata {
                name
                externalId
            }
            lastStart
            lastStop
            regionId
            rtmpPullUrl
            ingestType
            channelMode
            perpetual
            regionId
            tla {
                enabled
                datacenter {
                    id
                    name
                }
            }
            engine {
                override {
                    enabled
                    version
                }
            }
            useOrganizationOrigin
        }
    }
`;

export const GET_CHANNEL_ADMIN_PART = gql`
    query GetChannelAdminPartQuery($channelId: ID!, $organizationId: ID!) {
        channel(channelId: $channelId, organizationId: $organizationId) {
            channelId
            organizationId
            ingestProtocol
            ingestPullUrl
            organization {
                organizationId
                tla {
                    activated
                    datacenters {
                        id
                        name
                    }
                }
                channelSettings {
                    engine {
                        override {
                            enabled
                            image
                        }
                    }
                }
            }
            pricing {
                transcoded
                viewed
                drm
            }
            metadata {
                enhancedLogging {
                    enabled
                    percentage
                }
                insights {
                    enabled
                }
            }
            cdn {
                provider
                url
            }
            publicationConfig {
                originSecurity {
                    enabled
                }
            }
            tla {
                enabled
            }
            engine {
                override {
                    enabled
                    version
                    image
                }
            }
        }
    }
`;

export const GET_CHANNEL_QUERY = gql`
    query GetChannelQuery($channelId: ID!, $organizationId: ID!) {
        channel(channelId: $channelId, organizationId: $organizationId) {
            channelId
            channelStatus
            channelMode
            organizationId
            organization {
                organizationId
                name
                license
                tla {
                    activated
                    datacenters {
                        id
                        name
                    }
                }
                channelSettings {
                    hybridFallback {
                        enabled
                    }
                    perpetual {
                        enabled
                    }
                    drm {
                        enabled
                    }
                    engine {
                        override {
                            enabled
                            defaultVersion
                        }
                    }
                    domain {
                        override {
                            enabled
                            defaultDomain
                        }
                    }
                    theoads {
                        enabled
                    }
                }
            }
            metadata {
                name
                externalId
                customization {
                    targetLatency
                }
            }
            theoads {
                enabled
                assetKeys {
                    assetKey
                    adBreakSelectionParameters {
                        key
                        value
                    }
                    eabnAdTagParameters {
                        key
                        value
                    }
                }
                podDuration
            }
            region {
                regionId
                name
                awsRegion
            }
            tla {
                enabled
                datacenter {
                    id
                    name
                    tlaId
                    rtmpPushEnabled
                    sdiInputs {
                        persistentId
                        name
                        deviceNumber
                    }
                    ndiSources
                }
            }
            sdi {
                persistentId
                deviceNumber
                audioConnection
                profile
                videoConnection
                videoMode
                videoFormat
            }
            hls {
                enabled
            }
            ingestType
            ingestProtocol
            ingestPullUrl
            streamConfig {
                abr
                fps
                resolution
                bitrate
                bitrateMode
                publishUrl
                restream {
                    id
                    url 
                }
                skipTranscode
                audio {
                    lang
                    label
                    pid
                }
                captions {
                    language
                    label
                    channel
                }
                drm {
                    enabled
                    widevine {
                        licenseAcquisitionUrl
                    }
                    playready {
                        licenseAcquisitionUrl
                    }
                    fairplay {
                        licenseAcquisitionUrl
                        certificateUrl
                    }
                }
            }
            rtmpPushUrl
            rtmpPullUrl
            domain
            streamKey
            failoverId
            fallback {
                enabled
                src
                type
            }
            nextMaintenance
            maintenance {
                type
                slots {
                    day
                    time
                }
                interval {
                    period
                    amount
                    time
                }
            }
            lastStart
            lastStop
            lastErrorMessage
            hasNoStandardConfig
            manifestUrl
            analyzerWebsocketUrl
            useOrganizationOrigin
            perpetual
            ingestTimeout
            cdn {
                provider
                url
            }
            publicationConfig {
                geoBlocking {
                    enabled
                    countries
                    mode
                }
                ipBlocking {
                    enabled
                    cidrs
                    mode
                }
                tokenSecurity {
                    enabled
                    version
                    keys {
                        id
                    }
                }
                defaultDomain
            }
            schedulers {
                schedulerId
                name
                start
                end
                phase
            }
            currentRun {
                ingestTimeout
            }
            aliases {
                channelId
                active
                isDeleted
                failoverId
                fallback {
                    enabled
                    src
                    type
                }
                metadata {
                    name
                    externalId
                    customization {
                        targetLatency
                    }
                }
                parentChannelId
                organizationId
                hls {
                    enabled
                }
                cdn {
                    provider
                    url
                }
                publicationConfig {
                    geoBlocking {
                        enabled
                        countries
                        mode
                    }
                    ipBlocking {
                        enabled
                        cidrs
                        mode
                    }
                    defaultDomain
                    tokenSecurity {
                        enabled
                        version
                        keys {
                            id
                        }
                    }
                }
            }
        }
    }
`;

export const GET_CHANNEL_WITH_ALL_ALIASES_QUERY = gql`
    query GetChannelWithAllAliasesQuery($channelId: ID!, $organizationId: ID!) {
        channel(channelId: $channelId, organizationId: $organizationId) {
            channelId
            channelStatus
            channelMode
            organizationId
            tla {
                enabled
                networkingConfig {
                    websocketUrl
                }
            }
            metadata {
                name
            }
            domain
            region {
                regionId
                awsRegion
            }
            aliases(showDeleted: true) {
                channelId
                active
                isDeleted
                metadata {
                    name
                }
                parentChannel {
                    channelId
                    metadata {
                        name
                    }
                }
            }
        }
    }
`;

export const START_CHANNEL_MUTATION = gql`
    mutation StartChannelMutation($input: StartChannelInput!) {
        startChannel(input: $input) {
            channelId
            channelStatus
        }
    }
`;

export const STOP_CHANNEL_MUTATION = gql`
    mutation StopChannelMutation($input: StopChannelInput!) {
        stopChannel(input: $input) {
            channelId
            channelStatus
        }
    }
`;

export const DELETE_CHANNEL_MUTATION = gql`
    mutation DeleteChannelMutation($input: DeleteChannelInput!) {
        deleteChannel(input: $input) {
            channelId
            channelStatus
        }
    }
`;

export const UPDATE_CHANNEL_MUTATION = gql`
    mutation UpdateChannelMutation($input: UpdateChannelInput!) {
        updateChannel(input: $input) {
            channelId
            metadata {
                name
            }
        }
    }
`;

export const UPDATE_STREAM_CONFIG_MUTATION = gql`
    mutation UpdateStreamConfigMutation($input: UpdateStreamConfigInput!) {
        updateStreamConfig(input: $input) {
            channelId
        }
    }
`;

export const CREATE_CHANNEL_MUTATION = gql`
    mutation CreateChannelMutation($input: CreateChannelInput!) {
        createChannel(input: $input) {
            channelId
        }
    }
`;
